import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Mail, Linkedin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import InitialsAvatar from "@/components/InitialsAvatar";
import { useI18n } from "@/i18n/I18nContext";
import { TEAM_MEMBERS } from "@/data/team";

const TITLE_TR = {
  tr: { eyebrow: "EKİBİMİZ", title: "Liderlik kadrosu.", lead: "Yatırımcılar ve kurumsal müşteriler için güveni inşa eden uzman ekip.", viewProfile: "Profili Gör" },
  en: { eyebrow: "OUR TEAM", title: "Leadership.", lead: "The expert team building trust for investors and corporate clients.", viewProfile: "View Profile" },
  ar: { eyebrow: "فريقنا", title: "فريق القيادة.", lead: "الفريق الذي يبني الثقة للمستثمرين والعملاء.", viewProfile: "عرض الملف" },
  es: { eyebrow: "NUESTRO EQUIPO", title: "Liderazgo.", lead: "El equipo experto que construye confianza para inversionistas y clientes corporativos.", viewProfile: "Ver perfil" },
  ru: { eyebrow: "НАША КОМАНДА", title: "Руководство.", lead: "Экспертная команда, строящая доверие для инвесторов и клиентов.", viewProfile: "Профиль" },
  de: { eyebrow: "UNSER TEAM", title: "Führungsteam.", lead: "Das Expertenteam, das Vertrauen für Investoren und Unternehmen aufbaut.", viewProfile: "Profil ansehen" },
  fr: { eyebrow: "NOTRE ÉQUIPE", title: "Direction.", lead: "L'équipe d'experts qui bâtit la confiance pour investisseurs et clients corporate.", viewProfile: "Voir le profil" },
};

const TeamPage = () => {
  const { locale } = useI18n();
  const t = TITLE_TR[locale] || TITLE_TR.en;

  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); }, []);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="team-page">
      <Navbar />
      <main>
        <section className="relative hero-wash">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12 md:pt-20 md:pb-16">
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-[0.14em] uppercase text-slate-500 hover:text-[hsl(var(--primary))]">
              <ArrowLeft className="h-3.5 w-3.5" /> Home
            </Link>
            <div className="mt-3 text-[11px] font-semibold tracking-[0.18em] text-[hsl(var(--accent))] uppercase">{t.eyebrow}</div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-[hsl(var(--primary))]" data-testid="team-title">{t.title}</h1>
            <p className="mt-4 max-w-2xl text-base md:text-lg text-slate-700">{t.lead}</p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-[hsl(var(--tbs-cream-50))]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TEAM_MEMBERS.map((m, idx) => (
                <motion.div
                  key={m.slug}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.45, delay: idx * 0.05 }}
                >
                  <Card data-testid={`team-card-${m.slug}`} className="card-lift overflow-hidden bg-white border border-slate-200 rounded-2xl flex flex-col h-full">
                    <div className="aspect-square relative bg-slate-100">
                      {m.photo ? (
                        <img src={m.photo} alt={m.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <InitialsAvatar initials={m.initials} accent={m.accent} />
                      )}
                      <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[rgba(11,18,32,0.7)] to-transparent pointer-events-none" />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <div className="text-[11px] font-semibold tracking-[0.14em] text-[hsl(var(--accent))] uppercase">{m.role[locale] || m.role.en}</div>
                      <h3 className="mt-1.5 font-display text-2xl text-[hsl(var(--primary))]">{m.name}</h3>
                      <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-4">{(m.bio[locale] || m.bio.en).slice(0, 220)}{((m.bio[locale] || m.bio.en).length > 220) && "…"}</p>
                      <div className="mt-auto pt-5 flex items-center justify-between">
                        <Link to={`/ekibimiz/${m.slug}`} data-testid={`team-card-${m.slug}-view`} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))] hover:text-[hsl(var(--tbs-navy-800))]">
                          {t.viewProfile}
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                        <a href={`mailto:${m.contact.email}`} aria-label={`Email ${m.name}`} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:text-[hsl(var(--primary))] hover:border-[rgba(201,162,39,0.5)]">
                          <Mail className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default TeamPage;
